/**
 * Grounding-search rank tracking: the pause switch and the data cutoff.
 *
 * Since 2026-09-25 Google has served invalid ("poisoned") result pages to rank
 * trackers, DataForSEO included.
 * Public study (Oct 2026): https://nozzle.io/research/poisoned-google-serps/
 * Rank rows written from that day on cannot be trusted, so:
 *
 *  - the weekly re-check (weekly-property-maintenance, Phase B) is OFF unless
 *    `GROUNDING_SERP_TRACKING_ENABLED=true`,
 *  - report generation (finalize-discovery-report) skips its Google and Brave
 *    checks under the same switch and stores the no-rank list from
 *    `grounding-search-list.ts` instead of the gap analysis, and
 *  - the SEO Opportunities read paths drop rank rows measured/checked on or
 *    after `GROUNDING_SERP_SUSPECT_FROM`. Rows are never deleted or modified;
 *    the filter lives in the read path only.
 *
 * Browser-safe (no database import): the dashboard anchors its date windows on
 * the cutoff too, so it is re-exported from `@repo/core/client`.
 */

/**
 * First instant whose grounding-search rank data is suspect. Reads exclude
 * rows at or after it (`< cutoff`), so the last trusted day is 2026-09-24 UTC.
 */
export const GROUNDING_SERP_SUSPECT_FROM = new Date("2026-09-25T00:00:00.000Z");

/**
 * Kill switch for grounding-search rank tracking (Google via DataForSEO and
 * Brave), both the weekly re-check and the report-time checks. Default OFF: only the literal value "true"
 * (trimmed, any case) turns it on. Read at call time so it can be flipped with
 * an env change and toggled in tests.
 */
export function isGroundingSerpTrackingEnabled(): boolean {
	return (
		process.env.GROUNDING_SERP_TRACKING_ENABLED?.trim().toLowerCase() ===
		"true"
	);
}

/**
 * The end of a read window that must not reach past the cutoff: whichever is
 * earlier of `now` and `cutoff`. A null cutoff means "no cutoff".
 */
export function groundingSerpWindowEnd(
	cutoff: Date | null = GROUNDING_SERP_SUSPECT_FROM,
	now: Date = new Date(),
): Date {
	if (cutoff === null) return now;
	return cutoff.getTime() < now.getTime() ? cutoff : now;
}
